export type QuestionMessagePayload = {
  section: string;
  questionText: string;
  answerText?: string | null;
  answerAttachments?: Array<{
    fileName?: string;
    fileUrl: string;
    type?: string;
    mimeType?: string;
  }>;
  questionItems?: Array<{ section?: string; questionText?: string; question?: string }>;
};

export function parseQuestionMessageContent(
  raw: string
): { isQuestion: false } | ({ isQuestion: true } & QuestionMessagePayload) {
  const text = (raw || '').trim();
  if (!text.startsWith('{')) {
    return { isQuestion: false };
  }
  try {
    const data = JSON.parse(text) as Record<string, unknown>;
    if (typeof data !== 'object' || data === null) {
      return { isQuestion: false };
    }
    const questionText = String(data.questionText ?? data.question ?? '').trim();
    if (!questionText && !('questionText' in data) && !('question' in data)) {
      return { isQuestion: false };
    }
    const answerAttachments = Array.isArray(data.answerAttachments)
      ? data.answerAttachments
          .filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null)
          .map((row) => ({
            fileName: row.fileName ? String(row.fileName) : undefined,
            fileUrl: String(row.fileUrl || row.file_url || ''),
            type: row.type ? String(row.type) : undefined,
            mimeType: row.mimeType ? String(row.mimeType) : undefined,
          }))
          .filter((row) => row.fileUrl)
      : [];
    return {
      isQuestion: true,
      section: String(data.section || 'General'),
      questionText,
      answerText: data.answerText
        ? String(data.answerText)
        : data.answer
          ? String(data.answer)
          : null,
      answerAttachments,
    };
  } catch {
    return { isQuestion: false };
  }
}

export function formatQuestionInboxPreview(raw: string): string {
  const parsed = parseQuestionMessageContent(raw);
  if (!parsed.isQuestion) {
    return raw;
  }
  if (parsed.answerText) {
    return `Q · Answer: ${parsed.answerText}`;
  }
  return `Q · ${parsed.questionText}`;
}

export function resolveQuestionFromMessage(msg: {
  kind?: string;
  content?: string;
  question?: QuestionMessagePayload | null;
}): QuestionMessagePayload | null {
  if (msg.question?.questionText) {
    return {
      ...msg.question,
      questionItems: msg.question.questionItems,
    };
  }
  if (msg.kind === 'question') {
    const parsed = parseQuestionMessageContent(msg.content || '');
    if (parsed.isQuestion) {
      return {
        section: parsed.section,
        questionText: parsed.questionText,
        answerText: parsed.answerText,
      };
    }
  }
  const parsed = parseQuestionMessageContent(msg.content || '');
  if (parsed.isQuestion) {
    return {
      section: parsed.section,
      questionText: parsed.questionText,
      answerText: parsed.answerText,
    };
  }
  return null;
}
